import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Card,
  Typography,
  Button,
  Checkbox,
  Radio,
  InputNumber,
  Divider,
  Tag,
  Space,
  Input,
  Alert,
  Spin,
} from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { Product, ProductVariant, SelectedVariant, ProductOrderItem, VariantGroup, VariantSelection } from '../../../types/products';
import { useGetProductWithVariantsQuery } from '../../../services/productsApi';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface ProductVariantsModalProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: (orderItem: ProductOrderItem) => void;
  product: Product;
  initialValues?: {
    quantity: number;
    notes: string;
    variantSelections?: VariantSelection[];
  };
}

interface GroupState {
  // SingleSelect: selected variant id
  selectedId?: string;
  // MultiSelect: set of selected variant ids
  selectedIds: Set<string>;
  // QuantitySplit: map of variantId -> quantity
  quantities: Record<string, number>;
}

const ProductVariantsModal: React.FC<ProductVariantsModalProps> = ({
  visible,
  onCancel,
  onConfirm,
  product,
  initialValues,
}) => {
  const [quantity, setQuantity] = useState<number>(1);
  const [notes, setNotes] = useState<string>('');
  const [groupStates, setGroupStates] = useState<Record<string, GroupState>>({});

  const {
    data: productWithVariants,
    isLoading,
    error,
  } = useGetProductWithVariantsQuery(product.id, {
    skip: !visible,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const variantGroups = productWithVariants?.variantGroups ?? [];
  // Ungrouped variants (legacy, no group assigned)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ungroupedVariants = useMemo(() => {
    if (!productWithVariants) return [];
    const groupedIds = new Set(variantGroups.flatMap(g => g.variants.map(v => v.id)));
    return productWithVariants.variants.filter(v => !groupedIds.has(v.id));
  }, [productWithVariants, variantGroups]);

  // Reset state when modal opens/closes, or pre-fill from initialValues for editing
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (visible) {
      if (initialValues) {
        setQuantity(initialValues.quantity);
        setNotes(initialValues.notes);
        // Reconstruct groupStates from variantSelections
        const newGroupStates: Record<string, GroupState> = {};
        for (const sel of initialValues.variantSelections ?? []) {
          const gId = sel.groupId ?? '__ungrouped__';
          if (!newGroupStates[gId]) {
            newGroupStates[gId] = { selectedIds: new Set(), quantities: {} };
          }
          if (sel.quantity !== undefined) {
            newGroupStates[gId].quantities[sel.variantId] = sel.quantity;
          } else {
            // Populate both selectedId and selectedIds to handle
            // SingleSelect vs MultiSelect ambiguity
            newGroupStates[gId].selectedId = sel.variantId;
            newGroupStates[gId].selectedIds.add(sel.variantId);
          }
        }
        setGroupStates(newGroupStates);
      } else {
        setQuantity(1);
        setNotes('');
        setGroupStates({});
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Clean up groupStates if variants were deleted since the item was created
  useEffect(() => {
    if (!productWithVariants || !initialValues) return;
    setGroupStates(prev => {
      const cleaned: Record<string, GroupState> = {};
      for (const group of productWithVariants.variantGroups) {
        const state = prev[group.id];
        if (!state) continue;
        const validIds = new Set(group.variants.map(v => v.id));
        const newSelectedIds = new Set<string>();
        Array.from(state.selectedIds).forEach(id => {
          if (validIds.has(id)) newSelectedIds.add(id);
        });
        const newQuantities: Record<string, number> = {};
        for (const [id, qty] of Object.entries(state.quantities)) {
          if (validIds.has(id)) newQuantities[id] = qty;
        }
        cleaned[group.id] = {
          selectedId: state.selectedId && validIds.has(state.selectedId) ? state.selectedId : undefined,
          selectedIds: newSelectedIds,
          quantities: newQuantities,
        };
      }
      // Preserve ungrouped state
      if (prev['__ungrouped__']) {
        const groupedIds = new Set(productWithVariants.variantGroups.flatMap(g => g.variants.map(v => v.id)));
        const ungroupedIds = new Set(productWithVariants.variants.filter(v => !groupedIds.has(v.id)).map(v => v.id));
        const newSelectedIds = new Set<string>();
        Array.from(prev['__ungrouped__'].selectedIds).forEach(id => {
          if (ungroupedIds.has(id)) newSelectedIds.add(id);
        });
        cleaned['__ungrouped__'] = { selectedIds: newSelectedIds, quantities: {} };
      }
      return cleaned;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productWithVariants]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getGroupState = (groupId: string): GroupState => {
    return groupStates[groupId] || { selectedIds: new Set(), quantities: {} };
  };

  const updateGroupState = (groupId: string, update: Partial<GroupState>) => {
    setGroupStates(prev => ({
      ...prev,
      [groupId]: { ...getGroupState(groupId), ...update },
    }));
  };

  // Get all selected variants across all groups
  const getAllSelectedVariants = (): { variant: ProductVariant; quantity?: number; group?: VariantGroup }[] => {
    const result: { variant: ProductVariant; quantity?: number; group?: VariantGroup }[] = [];

    for (const group of variantGroups) {
      const state = getGroupState(group.id);

      if (group.selectionMode === 'SingleSelect') {
        if (state.selectedId) {
          const v = group.variants.find(v => v.id === state.selectedId);
          if (v) result.push({ variant: v, group });
        }
      } else if (group.selectionMode === 'MultiSelect') {
        for (const v of group.variants) {
          if (state.selectedIds.has(v.id)) {
            result.push({ variant: v, group });
          }
        }
      } else if (group.selectionMode === 'QuantitySplit') {
        for (const v of group.variants) {
          const qty = state.quantities[v.id];
          if (qty && qty > 0) {
            result.push({ variant: v, quantity: qty, group });
          }
        }
      }
    }

    // Ungrouped variants (legacy MultiSelect)
    const ungroupedState = getGroupState('__ungrouped__');
    for (const v of ungroupedVariants) {
      if (ungroupedState.selectedIds.has(v.id)) {
        result.push({ variant: v });
      }
    }

    return result;
  };

  // Calculate price
  const calculateUnitPrice = () => {
    const selected = getAllSelectedVariants();
    const nonSplitModifiers = selected
      .filter(s => !s.quantity)
      .reduce((sum, s) => sum + s.variant.priceModifier, 0);
    return product.price + nonSplitModifiers;
  };

  const calculateTotal = () => {
    const selected = getAllSelectedVariants();
    const hasQuantitySplit = selected.some(s => s.quantity);

    if (hasQuantitySplit) {
      // For QuantitySplit: each variant qty * (base + modifier)
      const nonSplitModifiers = selected
        .filter(s => !s.quantity)
        .reduce((sum, s) => sum + s.variant.priceModifier, 0);
      const splitTotal = selected
        .filter(s => s.quantity)
        .reduce((sum, s) => sum + (product.price + nonSplitModifiers + s.variant.priceModifier) * s.quantity!, 0);
      return splitTotal;
    }

    return calculateUnitPrice() * quantity;
  };

  // Validation
  const getValidationErrors = (): string[] => {
    const errors: string[] = [];

    for (const group of variantGroups) {
      const state = getGroupState(group.id);

      if (group.isRequired) {
        if (group.selectionMode === 'SingleSelect' && !state.selectedId) {
          errors.push(`Selecciona una opción en "${group.name}"`);
        }
        if (group.selectionMode === 'MultiSelect' && state.selectedIds.size === 0) {
          errors.push(`Selecciona al menos una opción en "${group.name}"`);
        }
        if (group.selectionMode === 'QuantitySplit') {
          const totalQty = Object.values(state.quantities).reduce((sum, q) => sum + q, 0);
          if (totalQty === 0) {
            errors.push(`Asigna cantidades en "${group.name}"`);
          }
        }
      }

      if (group.selectionMode === 'QuantitySplit') {
        const totalQty = Object.values(state.quantities).reduce((sum, q) => sum + q, 0);
        if (totalQty > 0 && totalQty !== quantity) {
          errors.push(`La suma en "${group.name}" debe ser ${quantity} (actual: ${totalQty})`);
        }
      }
    }

    return errors;
  };

  const isValid = quantity >= 1 && getValidationErrors().length === 0;

  const handleConfirm = () => {
    const selected = getAllSelectedVariants();

    // Build legacy selectedVariants for display
    const selectedVariants: SelectedVariant[] = selected.map(s => ({
      id: s.variant.id,
      name: s.variant.name,
      priceModifier: s.variant.priceModifier,
    }));

    // Build new variantSelections with quantity info
    const variantSelections: VariantSelection[] = selected.map(s => ({
      variantId: s.variant.id,
      name: s.variant.name,
      priceModifier: s.variant.priceModifier,
      quantity: s.quantity,
      groupId: s.group?.id,
      groupName: s.group?.name,
    }));

    const orderItem: ProductOrderItem = {
      productId: product.id,
      productName: product.name,
      quantity,
      selectedVariants,
      variantSelections: variantSelections.length > 0 ? variantSelections : undefined,
      unitPrice: calculateUnitPrice(),
      notes: notes.trim() || undefined,
      productionCenters: product.productionCenters,
    };

    // Override total for QuantitySplit
    const hasQuantitySplit = selected.some(s => s.quantity);
    if (hasQuantitySplit) {
      orderItem.total = calculateTotal();
    }

    onConfirm(orderItem);
    onCancel();
  };

  const validationErrors = getValidationErrors();

  const renderGroup = (group: VariantGroup) => {
    const state = getGroupState(group.id);

    return (
      <div key={group.id} style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Space>
            <Text strong>{group.name}</Text>
            {group.isRequired && <Text type="danger">*</Text>}
          </Space>
          <Tag color={group.selectionMode === 'SingleSelect' ? 'blue' : group.selectionMode === 'QuantitySplit' ? 'orange' : 'green'}>
            {group.selectionMode === 'SingleSelect' ? 'Elegir una' : group.selectionMode === 'QuantitySplit' ? 'Dividir cantidad' : 'Múltiple'}
          </Tag>
        </div>

        {group.selectionMode === 'SingleSelect' && (
          <Radio.Group
            value={state.selectedId}
            onChange={e => updateGroupState(group.id, { selectedId: e.target.value })}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {group.variants.map(v => (
                <Card key={v.id} size="small" style={{ border: state.selectedId === v.id ? '2px solid #1890ff' : undefined }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Radio value={v.id}>{v.name}</Radio>
                    {v.priceModifier !== 0 && (
                      <Text style={{ color: v.priceModifier >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }}>
                        {v.priceModifier >= 0 ? '+' : ''}{formatPrice(v.priceModifier)}
                      </Text>
                    )}
                  </div>
                </Card>
              ))}
            </Space>
          </Radio.Group>
        )}

        {group.selectionMode === 'MultiSelect' && (
          <Space direction="vertical" style={{ width: '100%' }}>
            {group.variants.map(v => (
              <Card key={v.id} size="small" style={{ border: state.selectedIds.has(v.id) ? '2px solid #1890ff' : undefined }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Checkbox
                    checked={state.selectedIds.has(v.id)}
                    onChange={e => {
                      const newSet = new Set(state.selectedIds);
                      if (e.target.checked) newSet.add(v.id);
                      else newSet.delete(v.id);
                      updateGroupState(group.id, { selectedIds: newSet });
                    }}
                  >
                    {v.name}
                  </Checkbox>
                  {v.priceModifier !== 0 && (
                    <Text style={{ color: v.priceModifier >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }}>
                      {v.priceModifier >= 0 ? '+' : ''}{formatPrice(v.priceModifier)}
                    </Text>
                  )}
                </div>
              </Card>
            ))}
          </Space>
        )}

        {group.selectionMode === 'QuantitySplit' && (
          <>
            <Space direction="vertical" style={{ width: '100%' }}>
              {group.variants.map(v => (
                <Card key={v.id} size="small" style={{ border: (state.quantities[v.id] || 0) > 0 ? '2px solid #fa8c16' : undefined }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text>{v.name}</Text>
                    <Space>
                      {v.priceModifier !== 0 && (
                        <Text style={{ color: v.priceModifier >= 0 ? '#52c41a' : '#ff4d4f', fontSize: 12 }}>
                          {v.priceModifier >= 0 ? '+' : ''}{formatPrice(v.priceModifier)}
                        </Text>
                      )}
                      <InputNumber
                        min={0}
                        max={quantity}
                        value={state.quantities[v.id] || 0}
                        onChange={val => {
                          updateGroupState(group.id, {
                            quantities: { ...state.quantities, [v.id]: val || 0 },
                          });
                        }}
                        size="small"
                        style={{ width: 70 }}
                      />
                    </Space>
                  </div>
                </Card>
              ))}
            </Space>
            {(() => {
              const totalQty = Object.values(state.quantities).reduce((sum, q) => sum + q, 0);
              const isMatch = totalQty === quantity;
              return (
                <div style={{ marginTop: 8, textAlign: 'right' }}>
                  <Text type={isMatch ? 'success' : 'danger'} strong>
                    Asignado: {totalQty}/{quantity} {isMatch ? '✓' : ''}
                  </Text>
                </div>
              );
            })()}
          </>
        )}
      </div>
    );
  };

  const hasGroups = variantGroups.length > 0;
  const hasUngrouped = ungroupedVariants.length > 0;

  return (
    <Modal
      title={initialValues ? 'Editar Producto' : 'Personalizar Producto'}
      open={visible}
      onCancel={onCancel}
      width={600}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancelar
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirm}
          disabled={!isValid}
        >
          {initialValues ? 'Guardar Cambios' : 'Agregar al Pedido'} - {formatPrice(calculateTotal())}
        </Button>,
      ]}
    >
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Product Info */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div style={{ flex: 1 }}>
              <Title level={4} style={{ margin: 0 }}>
                {product.name}
              </Title>
              {product.description && (
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  {product.description}
                </Text>
              )}
            </div>
            <div style={{ textAlign: 'right', marginLeft: 16 }}>
              <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                {formatPrice(product.price)}
              </Text>
              {product.preparationTimeMinutes && (
                <div>
                  <Tag color="blue" style={{ marginTop: 4 }}>
                    {product.preparationTimeMinutes} min
                  </Tag>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Loading and Error States */}
        {isLoading && (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text>Cargando modificadores...</Text>
            </div>
          </div>
        )}

        {error && (
          <Alert
            message="Error al cargar modificadores"
            description="No se pudieron cargar los modificadores del producto. Intenta de nuevo."
            type="error"
            style={{ marginBottom: 16 }}
          />
        )}

        {!isLoading && !error && (
          <>
            {/* Quantity Selection - show BEFORE groups so QuantitySplit knows the target */}
            <div style={{ marginBottom: 16 }}>
              <Title level={5}>Cantidad</Title>
              <Space>
                <Button
                  icon={<MinusOutlined />}
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                />
                <InputNumber
                  min={1}
                  max={99}
                  value={quantity}
                  onChange={(value) => setQuantity(value || 1)}
                  style={{ width: 80 }}
                />
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => setQuantity(Math.min(99, quantity + 1))}
                  disabled={quantity >= 99}
                />
              </Space>
            </div>

            <Divider />

            {/* Variant Groups */}
            {hasGroups && variantGroups.map(group => renderGroup(group))}

            {/* Ungrouped Variants (legacy) */}
            {hasUngrouped && (
              <div style={{ marginBottom: 16 }}>
                {hasGroups && <Divider />}
                <Title level={5}>Modificadores Disponibles</Title>
                <div>
                  {ungroupedVariants.map(variant => {
                    const state = getGroupState('__ungrouped__');
                    return (
                      <Card
                        key={variant.id}
                        size="small"
                        style={{
                          marginBottom: 8,
                          border: state.selectedIds.has(variant.id) ? '2px solid #1890ff' : undefined,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Checkbox
                            checked={state.selectedIds.has(variant.id)}
                            onChange={e => {
                              const newSet = new Set(state.selectedIds);
                              if (e.target.checked) newSet.add(variant.id);
                              else newSet.delete(variant.id);
                              updateGroupState('__ungrouped__', { selectedIds: newSet });
                            }}
                          >
                            <Text strong>{variant.name}</Text>
                          </Checkbox>
                          <Text
                            style={{
                              color: variant.priceModifier >= 0 ? '#52c41a' : '#ff4d4f',
                              fontWeight: 'bold',
                            }}
                          >
                            {variant.priceModifier >= 0 ? '+' : ''}
                            {formatPrice(variant.priceModifier)}
                          </Text>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {!hasGroups && !hasUngrouped && (
              <Alert
                message="Sin modificadores"
                description="Este producto no tiene modificadores disponibles."
                type="info"
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <Alert
                message="Completa las selecciones requeridas"
                description={
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {validationErrors.map((err, i) => <li key={i}>{err}</li>)}
                  </ul>
                }
                type="warning"
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Notes */}
            <div style={{ marginBottom: 16 }}>
              <Title level={5}>Notas Especiales (Opcional)</Title>
              <TextArea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ej: Sin cebolla, término medio, extra picante..."
                rows={3}
                maxLength={200}
                showCount
              />
            </div>

            {/* Price Summary */}
            <Card size="small" style={{ backgroundColor: 'var(--bg-success-light)', border: '1px solid var(--border-success)' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text>Precio base:</Text>
                  <Text>{formatPrice(product.price)}</Text>
                </div>
                {getAllSelectedVariants().filter(s => !s.quantity && s.variant.priceModifier !== 0).map(s => (
                  <div key={s.variant.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text style={{ fontSize: '12px' }}>+ {s.variant.name}:</Text>
                    <Text style={{ fontSize: '12px', color: s.variant.priceModifier >= 0 ? '#52c41a' : '#ff4d4f' }}>
                      {s.variant.priceModifier >= 0 ? '+' : ''}{formatPrice(s.variant.priceModifier)}
                    </Text>
                  </div>
                ))}
                <Divider style={{ margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text strong style={{ fontSize: '16px' }}>Total:</Text>
                  <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                    {formatPrice(calculateTotal())}
                  </Text>
                </div>
              </div>
            </Card>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ProductVariantsModal;
